import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { Chip } from '../../../components/ui/Chip';
import { Helper } from '../../../components/ui/Helper';
import { EmptyState } from '../../../components/ui/EmptyState';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow, SkeletonTable } from '../../../components/ui/Skeleton';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { MobileFilters } from '../../../components/ui/MobileFilters';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { isoDay } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { adminHref } from '../../../lib/adminRoutes';
import { SettlePopover } from '../SettlePopover';
import { TrashPopover } from '../TrashPopover';
import { IncassiTab } from '../IncassiTab';
import { SollecitaPopover } from './SollecitaPopover';
import {
  daIncassareQuery, dataEsatta, durata, etichettaRata, giorniDaOggi, giornoInParole, nomeBreve, quando, rataInParole,
  type DaIncassare, type GruppoIncasso, type RataDaIncassare,
} from './fattureApi';

/**
 * Fatture › Fatture da incassare (proposta § 2.2).
 *
 *   frase     calcolata: quante fatture sono in ritardo e la più vecchia
 *   fascia    Fatture in ritardo · Fatture che scadono entro 30 giorni ·
 *             Incassato dai clienti nell'anno (le parti di "Soldi che ci devono
 *             i clienti" della Situazione, che qui non si ripete)
 *   gruppi    in ritardo / entro 30 giorni / più avanti, una riga per rata
 *   azioni    Segna incassata (popover della rata; era "Incassa", ma non
 *             incassa niente: registra) · Prepara il sollecito (testo da
 *             copiare) · ⋯ Togli dall'elenco.
 *   banca     dal 24/9 il bonifico che torna del tutto (importo, nome, data,
 *             un solo bonifico possibile) segna la rata da solo dopo il sync
 *             Qonto (admin.autochiusura.ts). Qui resta "In banca c'è un
 *             bonifico che torna" (basta confermare) e, quando qualcosa non
 *             torna, il motivo scritto nel chip ("Il bonifico di GVF del 12
 *             aprile potrebbe pagare anche un'altra fattura: controlla quale e
 *             conferma").
 *
 * Le altre liste (fatture già incassate, fatture tolte dall'elenco) sono lo
 * stesso `?status=` di prima e si aprono dal menu della barra: stanno in
 * IncassiTab.
 */
const GRUPPI: Array<{ key: GruppoIncasso; titolo: string }> = [
  { key: 'RITARDO', titolo: 'Fatture in ritardo' },
  { key: 'PROSSIMI', titolo: 'Fatture che scadono entro 30 giorni' },
  { key: 'AVANTI', titolo: 'Fatture che scadono più avanti' },
];

const NOTA_CONFERMA = 'Confermata da Fatture da incassare: in banca c\'è il bonifico corrispondente.';

/**
 * Il bonifico che non torna del tutto, con il motivo in chiaro (campo aggiunto
 * dall'API il 24/9: il tipo di base sta in fattureApi.ts, fuori da questa
 * cartella). `scegli`: più bonifici possibili, si sceglie da Banca.
 */
interface DubbioRata {
  txId: string;
  settledOn: string | null;
  amount: number;
  counterparty: string | null;
  label: string;
  testo: string;
  scegli: boolean;
}
const dubbioDi = (r: RataDaIncassare): DubbioRata | null => (r as RataDaIncassare & { dubbio?: DubbioRata | null }).dubbio ?? null;

export function FattureDaIncassare({ canWrite, canReadDocs, showHistory, tabs }: { canWrite: boolean; canReadDocs: boolean; showHistory?: boolean; tabs: ReactNode }) {
  const [statusParam, setStatus] = useUrlState<string>('status', 'aperte');
  if (statusParam === 'incassate' || statusParam === 'cestinate') {
    return <IncassiTab status={statusParam} canWrite={canWrite} showHistory={showHistory} onBack={() => setStatus('aperte')} tabs={tabs} />;
  }
  return <DaIncassareAperte canWrite={canWrite} canReadDocs={canReadDocs} showHistory={showHistory} onAltra={setStatus} tabs={tabs} />;
}

function DaIncassareAperte({ canWrite, canReadDocs, showHistory, onAltra, tabs }: {
  canWrite: boolean; canReadDocs: boolean; showHistory?: boolean; onAltra: (s: 'incassate' | 'cestinate') => void; tabs: ReactNode;
}) {
  const q = useQuery(daIncassareQuery(showHistory));
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const mobile = useIsMobile();
  const { ignoreDocs } = useAdminActions();
  // Selezione per FATTURA (togliere dall'elenco vale per il documento, non per
  // la rata). Serve al caso vero per cui è nata: cinque fatture rifiutate
  // dall'Agenzia delle Entrate si tolgono in un colpo. Solo dal computer.
  const [selected, setSelected] = useState<string[]>([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const pickable = canWrite && !mobile;
  useEffect(() => { setSelected([]); setTrashOpen(false); }, [search, showHistory]);

  const data = q.data;
  const s = search.trim().toLowerCase();
  const items = useMemo(
    () => (data?.items ?? []).filter((r) => !s || `${r.invoiceNumber ?? ''} ${r.clientName}`.toLowerCase().includes(s)),
    [data, s],
  );
  const toggle = (id: string) => setSelected((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));

  if (q.isLoading && !data) {
    return (
      <>
        <SkeletonKPIRow count={3} withIcon={false} />
        {tabs}
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={5} cols={5} withAvatar={false} /></tbody></table></div>
      </>
    );
  }
  if (!data) return <>{tabs}<EmptyState brand title="Le fatture da incassare non si sono caricate" description="Riprova tra qualche secondo." /></>;

  return (
    <>
      <p className="fs-frase">{frase(data)}</p>

      <Fascia data={data} onIncassate={() => onAltra('incassate')} />

      {tabs}

      <MobileFilters attivi={s ? 1 : 0}>
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
            <Icon name="search" size={12} />
            <input aria-label="Cerca fra le fatture da incassare" placeholder="Cerca per numero di fattura o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <span className="mute text-[12px]">{items.length === 1 ? '1 rata da incassare' : `${items.length} rate da incassare`}</span>
          {pickable && selected.length > 0 && (
            <div className="relative">
              <button className="btn btn-sm" disabled={ignoreDocs.isPending} onClick={() => setTrashOpen((v) => !v)}>
                <Icon name="trash" size={10} /> Togli dall'elenco {selected.length === 1 ? '1 fattura' : `${selected.length} fatture`}
              </button>
              {trashOpen && (
                <TrashPopover
                  title={selected.length === 1 ? '1 fattura' : `${selected.length} fatture`}
                  cosa={selected.length === 1 ? 'fattura' : 'fatture'}
                  pending={ignoreDocs.isPending}
                  onClose={() => setTrashOpen(false)}
                  onConfirm={(v) => {
                    setTrashOpen(false);
                    const ids = selected;
                    setSelected([]);
                    ignoreDocs.mutate({ kind: 'invoice', ids, label: ids.length === 1 ? '1 fattura' : `${ids.length} fatture`, ...v });
                  }}
                />
              )}
            </div>
          )}
          <ActionsMenu
            variant="default"
            label="Altri elenchi di fatture"
            items={[
              { key: 'incassate', label: 'Fatture già incassate', icon: 'check', onClick: () => onAltra('incassate') },
              { key: 'cestinate', label: "Fatture tolte dall'elenco", icon: 'trash', onClick: () => onAltra('cestinate') },
            ]}
          />
        </div>
      </MobileFilters>

      {items.length === 0 ? (
        s ? (
          <EmptyState brand title="Nessuna fattura da incassare con questa ricerca" description="Prova con il numero della fattura o il nome del cliente."
            action={<button className="btn" onClick={() => setSearch('')}>Azzera la ricerca</button>} />
        ) : (
          <EmptyState brand title="Nessuna fattura da incassare" description="Tutte le rate delle fatture emesse risultano incassate." />
        )
      ) : (
        GRUPPI.map((g) => {
          const righe = items.filter((r) => r.gruppo === g.key);
          if (!righe.length) return null;
          // La colonna Rata si decide su tutta la lista, non sul gruppo: così
          // le colonne dei tre gruppi restano allineate una sotto l'altra.
          const conRata = items.some((r) => rataInParole(r.seq, r.ofTotal));
          const ids = [...new Set(righe.map((r) => r.invoiceId))];
          const tutte = ids.every((id) => selected.includes(id));
          return (
            <div key={g.key} className="ed-section fs-group">
              <div className="data-hd">
                <span className="t-micro">{g.titolo}</span>
              </div>
              {/* Scheda sul telefono. La riga non apre un dettaglio (il gesto è
                  Incassa, dentro la scheda): niente chevron, `tbl--static`. */}
              <table className="tbl tbl--ed tbl--card tbl--static">
                <thead>
                  <tr>
                    {pickable && (
                      <th style={{ width: 34 }}>
                        <input type="checkbox" aria-label={`Seleziona tutte: ${g.titolo.toLowerCase()}`} checked={tutte}
                          onChange={() => setSelected((x) => (tutte ? x.filter((id) => !ids.includes(id)) : [...new Set([...x, ...ids])]))} />
                      </th>
                    )}
                    <th>Cliente e fattura</th>
                    {conRata && <th style={{ width: 120 }}>Rata</th>}
                    <th style={{ width: 200 }}>Quando scade</th>
                    <th className="num" style={{ width: 130 }}>Importo</th>
                    {/* 330: "Segna incassata" + "Prepara il sollecito" + ⋯ in riga. */}
                    <th style={{ width: 330 }} />
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r) => (
                    <RigaRata key={r.id} r={r} conRata={conRata} canWrite={canWrite} canReadDocs={canReadDocs}
                      pickable={pickable} picked={selected.includes(r.invoiceId)} onPick={() => toggle(r.invoiceId)} />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Frase e fascia
// ════════════════════════════════════════════════════════════════════

/**
 * La frase in cima: conteggi, nomi e tempo in parole, mai importi (SPEC R1,
 * gli importi stanno nella fascia). Tono prudente: "con i dati di oggi".
 */
function frase(d: DaIncassare): ReactNode {
  const ritardo = d.items.filter((r) => r.gruppo === 'RITARDO');
  const prossimi = d.items.filter((r) => r.gruppo === 'PROSSIMI');
  const nRitardo = new Set(ritardo.map((r) => r.invoiceId)).size;
  const parti: ReactNode[] = [];

  if (nRitardo > 0) {
    const vecchia = ritardo[0]!;
    const g = giorniDaOggi(vecchia.dueDate) ?? 0;
    const daQuanto = g === -1 ? 'da ieri' : `da ${durata(g)}`;
    parti.push(nRitardo === 1
      ? <>Con i dati di oggi <strong>una fattura è in ritardo</strong>: quella di {nomeBreve(vecchia.clientName)}, {daQuanto}.</>
      : <>Con i dati di oggi <strong>{nRitardo} fatture sono in ritardo</strong>: la più vecchia è quella di {nomeBreve(vecchia.clientName)}, {daQuanto}.</>);
  } else if (prossimi.length > 0) {
    const prima = prossimi[0]!;
    parti.push(<>Con i dati di oggi nessuna fattura è in ritardo. La prossima a scadere è quella di {nomeBreve(prima.clientName)}, {quando(prima.dueDate)}.</>);
  } else if (d.items.length > 0) {
    parti.push(<>Con i dati di oggi nessuna fattura è in ritardo e nessuna scade nei prossimi 30 giorni.</>);
  } else {
    parti.push(<>Con i dati di oggi i clienti non ci devono niente: tutte le fatture emesse risultano incassate.</>);
  }

  const bonifici = d.totals.bonificiInBanca;
  const dubbi = d.items.filter((r) => dubbioDi(r)).length;
  if (bonifici > 0) {
    parti.push(<> Per {bonifici === 1 ? 'una' : bonifici} c'è già il bonifico in banca: basta confermarl{bonifici === 1 ? 'o' : 'i'}.</>);
  }
  if (dubbi > 0) {
    parti.push(dubbi === 1
      ? <> Per una in banca c'è un bonifico che non torna del tutto: il motivo è scritto sulla riga.</>
      : <> Per {dubbi} in banca c'è un bonifico che non torna del tutto: il motivo è scritto sulla riga.</>);
  }
  if (bonifici === 0 && dubbi === 0 && nRitardo > 0) {
    parti.push(<> Dalla riga puoi preparare il testo del sollecito.</>);
  }
  return <>{parti.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>;
}

function Fascia({ data, onIncassate }: { data: DaIncassare; onIncassate: () => void }) {
  const t = data.totals;
  const primaProssima = data.items.find((r) => r.gruppo === 'PROSSIMI');
  return (
    <div className="stat-band fs-fascia">
      <div>
        <div className="st-label">Fatture in ritardo</div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={t.ritardo.amount} format={fmtEur} /></div>
        <div className={`st-delta${t.ritardo.amount > 0 ? ' text-alert' : ''}`}>{t.ritardo.amount > 0 ? 'da sollecitare' : 'nessuna'}</div>
      </div>
      <div>
        <div className="st-label">Fatture che scadono entro 30 giorni</div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={t.prossimi.amount} format={fmtEur} /></div>
        <div className="st-delta" title={dataEsatta(primaProssima?.dueDate, 'La prima scade il')}>
          {primaProssima ? `la prima scade ${quando(primaProssima.dueDate)}` : 'nessuna'}
        </div>
      </div>
      <div className="st-click" role="button" tabIndex={0} onClick={onIncassate} onKeyDown={(e) => { if (e.key === 'Enter') onIncassate(); }}
        title="Apri le fatture già incassate">
        <div className="st-label">
          Incassato dai clienti nel {t.incassatoAnno.year}
          <Helper text={`Tutte le rate incassate dal 1 gennaio ${t.incassatoAnno.year}, per data di incasso: ci sono anche fatture emesse l'anno prima e pagate quest'anno.`} />
        </div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={t.incassatoAnno.amount} format={fmtEur} /></div>
        <div className="st-delta">{t.incassatoAnno.rate === 1 ? '1 pagamento ricevuto' : `${t.incassatoAnno.rate} pagamenti ricevuti`}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Riga
// ════════════════════════════════════════════════════════════════════

function RigaRata({ r, conRata, canWrite, canReadDocs, pickable, picked, onPick }: {
  r: RataDaIncassare; conRata: boolean; canWrite: boolean; canReadDocs: boolean; pickable: boolean; picked: boolean; onPick: () => void;
}) {
  const navigate = useNavigate();
  const { markPaid, confermaProposta, ignoreDocs } = useAdminActions();
  const [aperto, setAperto] = useState<null | 'incassa' | 'sollecita' | 'togli'>(null);
  const label = etichettaRata(r);
  const rata = rataInParole(r.seq, r.ofTotal);
  const ritardo = r.gruppo === 'RITARDO';
  const giorni = giorniDaOggi(r.dueDate);
  const b = r.bonifico;
  const dubbio = dubbioDi(r);

  const menu: ActionItem[] = [
    { key: 'apri', label: 'Apri la fattura', icon: 'file', onClick: () => navigate(`/invoices/${r.invoiceId}`), hidden: !canReadDocs },
    { key: 'sollecita', label: 'Prepara il sollecito', icon: 'mail', onClick: () => setAperto('sollecita'), hidden: ritardo },
    { key: 'togli', label: "Togli dall'elenco", icon: 'trash', danger: true, onClick: () => setAperto('togli'), hidden: !canWrite },
  ];

  return (
    <tr style={{ cursor: 'default' }} className={picked ? 'bg-paper-2' : undefined}>
      {pickable && (
        <td onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label={`Seleziona ${label}`} checked={picked} onChange={onPick} />
        </td>
      )}
      <td data-m="hd">
        <div className="font-semibold">{r.clientName}</div>
        <div className="text-[12px] mute">
          Fattura <span className="mono">{r.invoiceNumber ?? 's/n'}</span>
          {r.issueDate && <> del {fmtDate(r.issueDate)}</>}
        </div>
        {b && (
          <div className="fs-sugg">
            <Chip tone="ok" dot>
              <span title={`${fmtDate(b.settledOn)} · € ${fmtEur(b.amount, true)} · ${b.counterparty ?? b.label}`}>
                In banca c'è un bonifico che torna, del {giornoInParole(b.settledOn)}
              </span>
            </Chip>
            {canWrite && (
              <button className="btn btn-sm" disabled={confermaProposta.isPending}
                onClick={() => confermaProposta.mutate({ lato: 'INCASSO', rataId: r.id, txId: b.txId, paidDate: b.settledOn ?? isoDay(), label, importo: r.amount, nota: NOTA_CONFERMA })}>
                <Icon name="check" size={10} /> Conferma l'incasso
              </button>
            )}
          </div>
        )}
        {!b && dubbio && (
          <div className="fs-sugg">
            <Chip tone="warn" dot>
              <span title={`${fmtDate(dubbio.settledOn)} · € ${fmtEur(dubbio.amount, true)} · ${dubbio.counterparty ?? dubbio.label}`}>{dubbio.testo}</span>
            </Chip>
            {canWrite && (dubbio.scegli ? (
              <button className="btn btn-sm" title="Apri i movimenti del conto di questo cliente e collega quello giusto"
                onClick={() => navigate(adminHref('banca', { vista: 'movimenti', q: dubbio.counterparty ?? r.clientName, side: 'CREDIT', unmatched: '1' }))}>
                <Icon name="link" size={10} /> Scegli il bonifico
              </button>
            ) : (
              <button className="btn btn-sm" disabled={confermaProposta.isPending}
                title="Segna la rata incassata con questo bonifico"
                onClick={() => confermaProposta.mutate({ lato: 'INCASSO', rataId: r.id, txId: dubbio.txId, paidDate: dubbio.settledOn ?? isoDay(), label, importo: r.amount, nota: NOTA_CONFERMA })}>
                <Icon name="check" size={10} /> Conferma l'incasso
              </button>
            ))}
          </div>
        )}
      </td>
      {conRata && <td data-m={rata ? 'key' : 'off'} data-l="Rata" className="mute">{rata ?? ''}</td>}
      <td data-m="key" data-l="Quando scade" title={dataEsatta(r.dueDate)}>
        <span className={ritardo ? 'text-alert font-semibold' : giorni != null && giorni <= 1 ? 'text-warn' : 'mute'}>{quando(r.dueDate)}</span>
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center gap-1 relative">
          {canWrite && (
            <button className="btn btn-sm" disabled={markPaid.isPending} title="Registra l'incasso di questa rata in Masamune OS: non arriva nessun bonifico da qui" onClick={() => setAperto(aperto === 'incassa' ? null : 'incassa')}>
              <Icon name="check" size={10} /> Segna incassata
            </button>
          )}
          {ritardo && (
            <button className="btn btn-sm btn-ghost" title="Prepara il testo del sollecito da copiare: non parte nessuna mail" onClick={() => setAperto(aperto === 'sollecita' ? null : 'sollecita')}>
              <Icon name="mail" size={10} /> Prepara il sollecito
            </button>
          )}
          <ActionsMenu items={menu} />
          {aperto === 'incassa' && (
            <SettlePopover
              mode="incassa"
              amount={r.amount}
              subtitle={rata ? `${rata} della fattura ${r.invoiceNumber ?? ''}` : `fattura ${r.invoiceNumber ?? ''}`}
              target={{ kind: 'INVOICE_PAYMENT', id: r.id }}
              withNote
              pending={markPaid.isPending}
              onClose={() => setAperto(null)}
              onConfirm={(v) => {
                setAperto(null);
                markPaid.mutate({ paymentId: r.id, amount: r.amount, label, ...v });
              }}
            />
          )}
          {aperto === 'sollecita' && <SollecitaPopover rata={r} onClose={() => setAperto(null)} />}
          {aperto === 'togli' && (
            <TrashPopover
              title={r.invoiceNumber ?? 's/n'}
              pending={ignoreDocs.isPending}
              onClose={() => setAperto(null)}
              onConfirm={(v) => { setAperto(null); ignoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label, ...v }); }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}
